/**
 * THEME JSON CONFIG CSS VARS
 * The following default component scss variables can be overwritten via theme configuration per brand.
 * If you need to add new one, please make sure to set a default value.
 * JSON Definition for Component with name "Example": testCssVar: "20px"
 * Resulting CSS var: $example-test-css-var: 20px;
 */

$stage-default-radius: 0 !default;
$stage-default-shadow: none !default;
$stage-margin-desktop: 48px !default;
$stage-margin-tablet: 32px !default;
$stage-margin-tablet-or-smaller: 24px !default;
$stage-blurred-background: 10px !default;
$stage-indicator-border-radius: 0 !default;
$stage-indicator-height: 48px !default;
$stage-indicator-width: 48px !default;
$stage-indicator-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25) !default;
$stage-indicator-color-light: #ffffff !default;
$stage-indicator-color-dark: #000000 !default;
$stage-video-control-color-light: #ffffff !default;
$stage-video-control-color-dark: #000000 !default;

/* END THEME JSON CONFIG CSS VARS ******************************************************* */

$content-spacing: 15px;
$content-padding: 24px;
$content-width-wide: 100%;
$content-max-width-wide: 1200px;
$content-width-normal: 75%;
$content-max-width-normal: 900px;
$content-width-narrow: 50%;
$content-max-width-narrow: 600px;
$tiny-shadow: 0px 1px 1px rgba(57, 57, 58, 0.2);

@mixin min-max-height($min, $max) {
  min-height: $min;
  max-height: $max;
}

:host {
  // Estimation of height of header of the page. This value will be overwritten if a real calculated
  // height is passed down as attribute to the web component.
  // --header-height: 120px;
  --header-height: 0px;

  display: block;
  @include eon-typography();
}

.#{$prefix}stage {
  position: relative;
  width: 100%;
  overflow: hidden;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  height: calc(80vh - var(--header-height));

  &.#{$prefix}full-height {
    max-height: unset;
    height: calc(100vh - var(--header-height));

    .#{$prefix}full-height-indicator-wrapper {
      position: absolute;
      left: 50%;
      z-index: 2;
      height: 48px;
      cursor: pointer;
      bottom: 48px;

      @include for-size(tablet) {
        bottom: 40px;
        left: 47%;
      }

      @include for-size(phone) {
        bottom: 32px;
        left: 45%;
      }

      .#{$prefix}indicator-svg {
        border-radius: $stage-indicator-border-radius;
        height: $stage-indicator-height;
        width: $stage-indicator-width;

        &.#{$prefix}indicator-light {
          filter: drop-shadow($stage-indicator-shadow);

          .#{$prefix}indicator-path {
            transition: opacity 0.2s;
            fill: $stage-indicator-color-light;
            opacity: 0.6;
          }

          &:hover {
            .#{$prefix}indicator-path {
              fill: $stage-indicator-color-light;
              opacity: 1;
            }
          }
        }

        &.#{$prefix}indicator-dark {
          .#{$prefix}indicator-path {
            transition: opacity 0.2s;
            fill: $stage-indicator-color-dark;
            opacity: 0.6;
          }

          &:hover {
            .#{$prefix}indicator-path {
              fill: $stage-indicator-color-dark;
              opacity: 1;
            }
          }
        }
      }
    }

    .#{$prefix}content-wrapper {
      // 1. indicator distance from bottom + 2. height of indicator + 3. content distance from indicator

      margin-bottom: calc(48px + 48px + 48px);

      @include for-size(tablet) {
        margin-bottom: calc(40px + 48px + 48px);
      }

      @include for-size(phone) {
        margin-bottom: calc(32px + 48px + 48px);
      }
      .#{$prefix}stage-content {
        margin-bottom: 0 !important;
      }

      .#{$prefix}stage-content:not(.#{$prefix}has-colored-background):not(.#{$prefix}is-blurred-background) {
        padding-bottom: 0;
      }
    }
  }

  border-radius: $stage-default-radius;
  box-shadow: $stage-default-shadow;

  .#{$prefix}stage-video {
    min-width: 100%;
    min-height: 100%;
    position: absolute;
  }

  @include for-size("phone") {
    @include min-max-height(350px, 420px);

    .#{$prefix}stage-video {
      height: 100%;
      ::slotted(eon-ui-videoplayer) {
        height: 100%;
      }
    }
  }

  @include for-size("tablet") {
    @include min-max-height(350px, 400px);
  }

  @include for-size("desktop") {
    @include min-max-height(450px, 550px);
  }

  @include for-size("desktop-l-or-larger") {
    @include min-max-height(550px, 700px);
  }

  ::slotted(eon-ui-button),
  ::slotted(eon-ui-link) {
    margin-left: $content-padding !important;
  }

  ::slotted(eon-ui-button) {
    flex-shrink: 0;
  }

  &.#{$prefix}top {
    align-items: flex-start;
    .#{$prefix}has-colored-background {
      margin-top: $stage-margin-desktop !important;
      @include for-size(tablet) {
        margin-top: $stage-margin-tablet !important;
      }
      @include for-size(phone) {
        margin-top: $stage-margin-tablet-or-smaller !important;
      }
    }

    .#{$prefix}stage-content {
      padding-top: 48px;
    }
  }

  &.#{$prefix}bottom {
    align-items: flex-end;
    .#{$prefix}has-colored-background {
      margin-bottom: $stage-margin-desktop !important;
      @include for-size(tablet) {
        margin-bottom: $stage-margin-tablet !important;
      }
      @include for-size(phone) {
        margin-bottom: $stage-margin-tablet-or-smaller !important;
      }
    }

    .#{$prefix}stage-content {
      padding-bottom: 48px;
    }
  }

  .#{$prefix}content-wrapper {
    margin: 0 $stage-margin-desktop;
    z-index: 2;
    display: flex;
    width: 1200px;

    @include for-size(desktop-l-or-larger) {
      margin: 0 auto;
    }

    @include for-size(tablet-or-smaller) {
      margin: 0 $stage-margin-tablet-or-smaller 0 $stage-margin-tablet-or-smaller;
    }

    @include for-size(tablet) {
      margin: 0 $stage-margin-tablet 0 $stage-margin-tablet;
    }
  }

  .#{$prefix}stage-content {
    display: flex;
    flex-direction: column;
    padding: 0;
    z-index: 1;

    &.#{$prefix}wide {
      width: $content-width-wide;
      margin-left: auto;
    }

    &.#{$prefix}normal {
      width: $content-width-normal;
      margin-left: auto;

      @include for-size(phone) {
        width: $content-width-wide;
      }
    }

    &.#{$prefix}narrow {
      width: $content-width-narrow;
      margin-left: auto;

      @include for-size(phone) {
        width: $content-width-wide;
      }
      @include for-size(tablet) {
        width: $content-width-normal;
      }
    }
    &.#{$prefix}has-colored-background {
      &.#{$prefix}is-blurred-background {
        background-color: v(contentBoxBackgroundColorBlurred);
        backdrop-filter: blur($stage-blurred-background);
      }
      background-color: v(contentBoxBackgroundColor);
      padding: $stage-margin-desktop;
      @include for-size(tablet) {
        padding: $stage-margin-tablet;
      }
      @include for-size(phone) {
        padding: $stage-margin-tablet-or-smaller;
      }
    }

    ::slotted(eon-ui-headline) {
      z-index: 99 !important; //MS Edge needs that
      display: block;
    }

    .#{$prefix}subheadline {
      @include eon-typography(abstract);
      margin: unset;
      text-align: right;
      color: v(subheadlineColor);

      &.#{$prefix}shadow {
        text-shadow: $tiny-shadow;
      }
      &.#{$prefix}left {
        text-align: left;
      }
      &.#{$prefix}center {
        text-align: center;
      }
      &.#{$prefix}right {
        text-align: right;
      }
    }

    .#{$prefix}stage-ctas {
      display: flex;
      align-items: center;
      flex-direction: row;
      justify-content: flex-end;
      z-index: 1;
      margin-top: 33px;
      row-gap: 15px;

      @include for-size(phone) {
        width: auto;
        flex-wrap: wrap;
        margin: 17px 0 0;

        ::slotted(eon-ui-button[full-width-on-mobile="true"]) {
          width: 100%;
          text-align: right;
        }
      }
      @include for-size(tablet) {
        margin-top: 25px;
      }
    }

    & > *:not(:last-child) {
      margin-bottom: $content-spacing;
    }
    &.#{$prefix}left {
      align-items: flex-start;
      margin-left: unset;
      margin-right: auto;
      ::slotted(*:first-of-type) {
        margin-left: unset !important;
        margin-right: $content-padding !important;
      }
      ::slotted(*:last-of-type) {
        margin-left: unset !important;
      }
      .#{$prefix}stage-ctas {
        justify-content: flex-start;
      }
    }
    &.#{$prefix}center {
      align-items: center;
      margin: auto;
      .#{$prefix}stage-ctas {
        justify-content: center;
      }
    }
    &.#{$prefix}right {
      align-items: flex-end;
      margin-left: auto;
      .#{$prefix}stage-ctas {
        justify-content: flex-end;
      }
    }
  }

  &.#{$prefix}has-violator {
    .#{$prefix}stage-content {
      padding-top: 16px;
    }

    .#{$prefix}stage-violator {
      position: absolute;
      top: 32px;

      &.#{$prefix}left {
        left: 32px;
      }

      &.#{$prefix}right {
        right: 32px;
      }

      @include for-size(tablet-or-smaller) {
        top: 24px;

        &.#{$prefix}left {
          left: 24px;
        }

        &.#{$prefix}right {
          right: 24px;
        }
      }
    }

    &.#{$prefix}violator-xlarge {
      .#{$prefix}content-wrapper {
        margin-top: calc(112px + 32px);

        @include for-size(tablet-or-smaller) {
          margin-top: calc(112px + 24px);
        }
      }
    }

    &.#{$prefix}violator-large {
      .#{$prefix}content-wrapper {
        margin-top: calc(80px + 32px);

        @include for-size(tablet-or-smaller) {
          margin-top: calc(80px + 24px);
        }
      }
    }

    &.#{$prefix}violator-small {
      .#{$prefix}content-wrapper {
        margin-top: calc(68px + 32px);

        @include for-size(tablet-or-smaller) {
          margin-top: calc(68px + 24px);
        }
      }
    }
  }

  .#{$prefix}icon-text-wrapper {
    position: absolute;
    bottom: 48px;
    left: 11%;
    display: flex;
    gap: 4px;
    align-items: center;
    background: none;
    border: none;
    margin: 0;
    padding: 0 8px 0 0;

    @include for-size(tablet-or-smaller) {
      bottom: 0;
    }

    .#{$prefix}icon-wrapper {
      svg {
        display: block;
      }
    }

    .#{$prefix}text {
      font-family: "EONBrixSans";
      font-style: normal;
      height: 18px;
      font-weight: 700;
      font-size: 14px;
      line-height: 18px;
      padding-top: 3px;
    }

    &.#{$prefix}video-control-light {
      &:focus {
        outline: $stage-video-control-color-light solid 2px;
        outline-offset: 4px;
      }
      path {
        fill: $stage-video-control-color-light;
      }
      .#{$prefix}text {
        color: $stage-video-control-color-light;

        &:hover {
          border-bottom: 2px solid $stage-video-control-color-light;
        }
      }
    }

    &.#{$prefix}video-control-dark {
      &:focus {
        outline: $stage-video-control-color-dark solid 2px;
        outline-offset: 4px;
      }
      path {
        fill: $stage-video-control-color-dark;
      }
      .#{$prefix}text {
        color: $stage-video-control-color-dark;

        &:hover {
          border-bottom: 2px solid $stage-video-control-color-dark;
        }
      }
    }
  }
}
